import { Trash2, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useModels, useDeleteModel } from '@/api/hooks/useModels';

export function ModelsPage() {
  const { data, isLoading } = useModels();
  const deleteModel = useDeleteModel();

  if (isLoading) {
    return (
      <div
        style={{
          height: 384,
          background: '#e2e8f0',
          borderRadius: 8,
          animation: 'pulse 2s infinite',
        }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Models</h1>
        <Link to="/models/new">
          <Button>
            <Plus size={16} />
            <span>Add Model</span>
          </Button>
        </Link>
      </div>

      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        <table>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <Th>Name</Th>
              <Th>Provider</Th>
              <Th align="right">Max Input Tokens</Th>
              <Th align="right">Max Output Tokens</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {data?.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    padding: 32,
                    textAlign: 'center',
                    fontSize: 13,
                    color: '#64748b',
                  }}
                >
                  No models configured
                </td>
              </tr>
            ) : (
              data?.map((model) => (
                <tr key={model.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                  <Td>
                    <Link
                      to={`/models/${encodeURIComponent(model.id)}`}
                      style={{ color: '#0f172a', fontWeight: 500, textDecoration: 'none' }}
                    >
                      {model.id}
                    </Link>
                  </Td>
                  <Td>
                    <span style={{ color: '#64748b' }}>{model.owned_by ?? '-'}</span>
                  </Td>
                  <Td align="right">
                    {model.max_input_tokens?.toLocaleString() ?? '-'}
                  </Td>
                  <Td align="right">
                    {model.max_output_tokens?.toLocaleString() ?? '-'}
                  </Td>
                  <Td align="right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete model ${model.id}?`)) {
                          deleteModel.mutate(model.id);
                        }
                      }}
                    >
                      <Trash2 size={16} color="#dc2626" />
                    </Button>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '12px 16px',
        textAlign: align ?? 'left',
        fontSize: 13,
        fontWeight: 500,
        color: '#475569',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      style={{
        padding: '12px 16px',
        textAlign: align ?? 'left',
        fontSize: 13,
      }}
    >
      {children}
    </td>
  );
}
